import type { BrowserViewerInput, BrowserViewFrameHeader } from "@afar-research/contracts";

export interface ViewRect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Where a frame lands inside the view: scaled to fit, centred, never stretched.
 * The page renders at the panel's own size in fill mode, so this is usually the
 * whole view; a device-sized viewport letterboxes.
 */
export function frameDrawRect(view: ViewRect, frame: BrowserViewFrameHeader): ViewRect {
  const scale = Math.min(view.width / frame.deviceWidth, view.height / frame.deviceHeight, 1);
  const width = frame.deviceWidth * scale;
  const height = frame.deviceHeight * scale;
  return {
    left: (view.width - width) / 2,
    top: (view.height - height) / 2,
    width,
    height,
  };
}

/**
 * A pointer position in the view, as CSS pixels in the page's viewport — the
 * coordinates CDP input expects. `null` outside the drawn page, so a click in
 * the letterbox does not reach the page's edge.
 */
export function pagePointFromView(
  view: ViewRect,
  frame: BrowserViewFrameHeader,
  clientX: number,
  clientY: number,
): { readonly x: number; readonly y: number } | null {
  const drawn = frameDrawRect(view, frame);
  const localX = clientX - view.left - drawn.left;
  const localY = clientY - view.top - drawn.top;
  if (localX < 0 || localY < 0 || localX > drawn.width || localY > drawn.height) return null;
  return {
    x: (localX / drawn.width) * frame.deviceWidth,
    y: (localY / drawn.height) * frame.deviceHeight,
  };
}

/** The view position of a page point, for drawing the agent cursor over the frame. */
export function viewPointFromPage(
  view: ViewRect,
  frame: BrowserViewFrameHeader,
  x: number,
  y: number,
): { readonly left: number; readonly top: number } {
  const drawn = frameDrawRect(view, frame);
  return {
    left: drawn.left + (x / frame.deviceWidth) * drawn.width,
    top: drawn.top + (y / frame.deviceHeight) * drawn.height,
  };
}

interface ModifierState {
  readonly altKey: boolean;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly shiftKey: boolean;
}

/** CDP's modifier bitmask: Alt=1, Control=2, Meta=4, Shift=8. */
export function cdpModifiers(event: ModifierState): number {
  return (
    (event.altKey ? 1 : 0) |
    (event.ctrlKey ? 2 : 0) |
    (event.metaKey ? 4 : 0) |
    (event.shiftKey ? 8 : 0)
  );
}

export function cdpMouseButton(button: number): "left" | "middle" | "right" | "none" {
  return button === 0 ? "left" : button === 1 ? "middle" : button === 2 ? "right" : "none";
}

/**
 * A keyboard event as CDP key input. Printable keys carry their text so the page
 * types them; a chord with Control or Meta does not, so it reaches the page as
 * a shortcut rather than as a character.
 */
export function cdpKeyInput(
  event: ModifierState & { readonly key: string; readonly code: string; readonly keyCode: number },
  phase: "down" | "up",
): Extract<BrowserViewerInput, { kind: "key" }> {
  const printable = event.key.length === 1 && !event.ctrlKey && !event.metaKey;
  return {
    kind: "key",
    type: phase === "up" ? "keyUp" : printable ? "keyDown" : "rawKeyDown",
    key: event.key,
    code: event.code,
    windowsVirtualKeyCode: event.keyCode,
    modifiers: cdpModifiers(event),
    ...(phase === "down" && printable ? { text: event.key === "Enter" ? "\r" : event.key } : {}),
  };
}
